import {
  DestroyRef,
  effect,
  inject,
  Injectable,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '@auth0/auth0-angular';
import { fromUnixTime, isPast } from 'date-fns';
import { fromEvent, interval } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { WEBSOCKET_RECONNECT_DELAY } from 'src/app/app.constants';
import { getNextPingPongTime } from 'src/app/shared/utils/date.utils';
import { QueryParamName } from 'src/app/shared/enums/query-param-name.enum';
import { WSEntity } from 'src/app/shared/enums/ws.enums';
import {
  WsAction,
  WsMessageModel,
  WsSubscribeAs,
} from 'src/app/shared/models/ws.models';
import { environment } from 'src/environments/environment';
import { RouteStore } from '../store/route.store';
import { WsStore } from '../store/ws.store';
import { WsMessageRouter } from './ws-message-router.service';
import { ErrorTypes } from '../models/error-types.model';
import { ErrorService } from './error.service';

/* The WebSocket transport: opens the socket once authenticated, keeps it alive
 * through ping/pong and reconnects, and re-subscribes as the route changes.
 * Handing the frames to the stores is WsMessageRouter's job. */
@Injectable({ providedIn: 'root' })
export class WsService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly auth = inject(AuthService);
  private readonly errorService = inject(ErrorService);

  private readonly wsStore = inject(WsStore);
  private readonly routeStore = inject(RouteStore);
  private readonly messageRouter = inject(WsMessageRouter);

  ws: WebSocketSubject<WsMessageModel> | null = null;
  wsCompleted = false;
  websocketRetry = 0;
  websocketRetryMax = 10;

  constructor() {
    /* Old wsInit$: open the socket once authenticated and restore the
     * previous subscription. */
    this.auth.isAuthenticated$
      .pipe(
        filter(isAuth => isAuth === true),
        take(1),
        switchMap(() => this.auth.getAccessTokenSilently()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(token => {
        this.runWebSocket(token);

        const currentlySubscribed = this.wsStore.currentlySubscribed();
        if (currentlySubscribed?.id && currentlySubscribed?.subscribedAs) {
          this.subscribe(
            currentlySubscribed.id,
            currentlySubscribed.subscribedAs,
          );
        }
      });

    /* Old wsPingPongCheck$ */
    interval(1000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (
          this.ws != null &&
          isPast(fromUnixTime(this.wsStore.nextPingPong()))
        ) {
          this.wsStore.pingPongUpdate(getNextPingPongTime());
          this.ws?.next({ action: WsAction.PING });
        }
      });

    /* Old wsPingPongCheckOnFocus$ */
    fromEvent(document, 'visibilitychange')
      .pipe(
        filter(() => document.visibilityState === 'visible'),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        if (this.wsCompleted) {
          this.runWebSocket();
        }

        this.wsStore.pingPongUpdate(getNextPingPongTime());
        this.ws?.next({ action: WsAction.PING });
      });

    /* Old recognizeRoute$: (re)subscribe per navigation. Tracks only the
     * route signals; the body reads and writes WsStore, so it must stay
     * untracked to avoid retriggering itself. */
    effect(() => {
      const params = this.routeStore.params();
      const isSubscribableRoute = this.routeStore.isSubscribableRoute();

      untracked(() => {
        const currentlySubscribed = this.wsStore.currentlySubscribed();

        if (
          this.ws &&
          currentlySubscribed &&
          currentlySubscribed.subscribedAs
        ) {
          this.ws.next({
            action: WsAction.UNSUB,
            entity: WSEntity.ANNOTATION,
            ...(currentlySubscribed.subscribedAs === WsSubscribeAs.REC
              ? { recordingId: currentlySubscribed.id }
              : { annotationId: currentlySubscribed.id }),
          });

          this.wsStore.unsubscribe(getNextPingPongTime());
        }

        if (isSubscribableRoute) {
          const { annotationId } = params;

          this.subscribe(
            params[QueryParamName.RecordingId]
              ? params[QueryParamName.RecordingId]
              : annotationId,
            params[QueryParamName.RecordingId]
              ? WsSubscribeAs.REC
              : WsSubscribeAs.ANN,
          );
        }
      });
    });
  }

  private subscribe(id: string, subscribedAs: WsSubscribeAs): void {
    this.wsStore.subscribe(id, subscribedAs, getNextPingPongTime());

    if (this.ws) {
      this.ws.next({
        action: WsAction.SUB,
        entity: WSEntity.ANNOTATION,
        ...(subscribedAs === WsSubscribeAs.REC
          ? { recordingId: id }
          : { annotationId: id }),
      });
    }
  }

  runWebSocket(token?: string): void {
    if (this.ws) {
      return;
    }

    if (token) {
      this.startWebSocket(token);
    } else {
      this.auth
        .getAccessTokenSilently()
        .pipe(take(1))
        .subscribe({
          // A missing token is treated like a failed one: retry rather than
          // opening a socket with "undefined" where the token belongs.
          next: token =>
            token ? this.startWebSocket(token) : this.reconnectWebSocket(),
          error: () => this.reconnectWebSocket(),
        });
    }
  }

  startWebSocket(token: string): void {
    this.ws = webSocket({
      url: `${environment.wsUrl}${token}`,
      openObserver: {
        next: () => {
          this.websocketRetry = 0;
        },
      },
    });

    this.ws.subscribe({
      next: msg => this.messageRouter.resolveWsMessage(msg),
      error: () => this.reconnectWebSocket(),
      complete: () => this.reconnectWebSocket(),
    });
  }

  reconnectWebSocket(): void {
    this.ws = null;

    if (this.websocketRetry < this.websocketRetryMax) {
      setTimeout(() => {
        this.runWebSocket();
      }, WEBSOCKET_RECONNECT_DELAY);
      this.websocketRetry++;
    } else {
      this.errorService.handleError(ErrorTypes.WEBSOCKET_DISCONNECTED);
    }
  }
}
